import { isAxiosError } from "axios";

/**
 * What a failed BDC call said. The BDC endpoints answer in Thai (`message`); a 409 of the INV
 * flow also carries `error.code` (BDC_INVOICE_*) and, on a number collision, `error.invoice_no`.
 */
export interface BdcApiError {
  status: number | null;
  code: string | null;
  message: string;
  invoiceNo: string | null;
  /** The POs a refusal names (BDC_EDI_*: error.po_numbers). */
  poNumbers: string[];
}

const textOf = (value: unknown): string | null =>
  typeof value === "string" && value.trim() !== "" ? value : null;

export const readBdcError = (error: unknown, fallback: string): BdcApiError => {
  if (!isAxiosError(error)) return { status: null, code: null, message: fallback, invoiceNo: null, poNumbers: [] };
  if (!error.response) {
    return {
      status: null,
      code: null,
      message: "ติดต่อเซิร์ฟเวอร์ไม่ได้ กรุณาตรวจสอบอินเทอร์เน็ตแล้วลองใหม่",
      invoiceNo: null,
      poNumbers: [],
    };
  }
  const body = error.response.data as { message?: unknown; error?: unknown } | undefined;
  const detail = body?.error && typeof body.error === "object" ? (body.error as Record<string, unknown>) : null;
  const code = textOf(detail?.code);
  return {
    status: error.response.status,
    code,
    message:
      textOf(body?.message) ??
      (code ? BDC_ERROR_TEXT[code] : undefined) ??
      textOf(body?.error) ??
      `${fallback} (Error ${error.response.status})`,
    invoiceNo: textOf(detail?.invoice_no),
    poNumbers: Array.isArray(detail?.po_numbers)
      ? (detail?.po_numbers as unknown[]).filter((po): po is string => typeof po === "string")
      : [],
  };
};

/** Fallback text per `error.code`, copied from the backend's response enum. */
export const BDC_ERROR_TEXT: Record<string, string> = {
  BDC_INVOICE_REQUIRES_DO_CONFIRMED: "ออก INV ได้เฉพาะ PO ที่ยืนยัน DO แล้ว",
  BDC_INVOICE_ALREADY_ISSUED: "PO นี้ออก INV ไปแล้ว",
  BDC_INVOICE_NO_ALREADY_USED:
    "เลข INV ถัดไปเคยใช้แล้ว ระบบหยุดไม่ข้ามเลขเอง ให้ MASTER Set เลขถัดไปใหม่ในหน้าตั้งค่า BDC",
  BDC_INVOICE_NO_EXHAUSTED: "เลข INV ครบ 9 หลักแล้ว ให้ MASTER Set หรือ Reset เลขถัดไป",
  BDC_INVOICE_SHIPMENT_CHANGED: "PO นี้ถูกเปลี่ยนสถานะระหว่างออก INV กรุณาโหลดหน้าใหม่แล้วลองอีกครั้ง",
  BDC_EDI_TEST_MODE: "สาขาที่อยู่ในโหมดทดสอบ (INV TB…) สร้างไฟล์ส่ง 7-11 ไม่ได้",
  BDC_EDI_INVOICE_REQUIRES_INVOICED:
    "สร้างไฟล์ Invoice ได้เฉพาะ PO ที่ออก INV แล้วและยังไม่บันทึกรอนำส่ง 7-11 (ไฟล์เดิม download ซ้ำได้จากประวัติ)",
  BDC_EDI_DO_REQUIRES_DO_CONFIRMED: "สร้างไฟล์ DO ได้เฉพาะ PO ที่ยืนยัน DO แล้ว",
  BDC_EDI_ORIGINAL_MISSING: "ไม่พบไฟล์ต้นฉบับ ORDS/PICKLST ของ PO นี้ ให้ยกเลิก INV ยกเลิกยืนยัน DO แล้วนำเข้าไฟล์ใหม่",
  BDC_EDI_ORIGINAL_MISMATCH: "ไฟล์ต้นฉบับไม่ตรงกับข้อมูล PO ในระบบ สร้างไฟล์ส่ง 7-11 ไม่ได้",
  BDC_EDI_SHIPMENT_CHANGED: "มี PO ถูกเปลี่ยนสถานะระหว่างสร้างไฟล์ กรุณาโหลดหน้าใหม่แล้วลองอีกครั้ง",
};

/**
 * What the screen does after a failed ออก INV:
 *   sequence — the running number is used / exhausted → ตั้งค่า BDC › เลขเอกสาร (Set / Reset)
 *   reload   — the PO moved under us (changed, already issued, not confirmed) → load it again
 *   other    — anything else; the message says why
 */
export type BdcIssueFailureKind = "sequence" | "reload" | "other";

export const issueFailureKind = (failure: BdcApiError): BdcIssueFailureKind => {
  switch (failure.code) {
    case "BDC_INVOICE_NO_ALREADY_USED":
    case "BDC_INVOICE_NO_EXHAUSTED":
      return "sequence";
    case "BDC_INVOICE_SHIPMENT_CHANGED":
    case "BDC_INVOICE_ALREADY_ISSUED":
    case "BDC_INVOICE_REQUIRES_DO_CONFIRMED":
      return "reload";
    default:
      return "other";
  }
};
